const BASE = 'https://security.ncore.kg/api/ncoreboard';

function authHeaders() {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                 : { 'Content-Type': 'application/json' };
}

async function request(path, options = {}) {
    const res = await fetch(`${BASE}${path}`, {
        headers: authHeaders(),
        ...options,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || err.error || JSON.stringify(err));
    }
    if (res.status === 204) return null;
    return res.json();
}

// ── Repositories ──────────────────────────────────────────────
export const listRepositories = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/repositories/${qs ? '?' + qs : ''}`);
};
export const getRepository = (id) => request(`/repositories/${id}/`);
export const getRepositoryBySlug = async (owner, slug) => {
    const data = await listRepositories({ owner, slug, page_size: 1 });
    const results = data.results || data;
    if (!results.length) throw new Error('Репозиторий не найден');
    return results[0];
};
export const createRepository = (data) => request('/repositories/', { method: 'POST', body: JSON.stringify(data) });
export const updateRepository = (id, data) => request(`/repositories/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteRepository = (id) => request(`/repositories/${id}/`, { method: 'DELETE' });
export const starRepository = (id) => request(`/repositories/${id}/star/`, { method: 'POST' });
export const watchRepository = (id) => request(`/repositories/${id}/watch/`, { method: 'POST' });
export const forkRepository = (id) => request(`/repositories/${id}/fork/`, { method: 'POST' });
export const archiveRepository = (id) => request(`/repositories/${id}/archive/`, { method: 'POST' });
export const getInsights = (id) => request(`/repositories/${id}/insights/`);
export const getCollaborators = (id) => request(`/repositories/${id}/collaborators/`);
export const addCollaborator = (id, data) => request(`/repositories/${id}/collaborators/add/`, { method: 'POST', body: JSON.stringify(data) });

// ── Branches ──────────────────────────────────────────────────
export const listBranches = (repoId) => request(`/repositories/${repoId}/branches/`);
export const createBranch = (repoId, data) => request(`/repositories/${repoId}/branches/`, { method: 'POST', body: JSON.stringify(data) });
export const deleteBranch = (repoId, branchId) => request(`/repositories/${repoId}/branches/${branchId}/`, { method: 'DELETE' });
export const setDefaultBranch = (repoId, branchId) => request(`/repositories/${repoId}/branches/${branchId}/set-default/`, { method: 'POST' });
export const protectBranch = (repoId, branchId) => request(`/repositories/${repoId}/branches/${branchId}/protect/`, { method: 'POST' });

// ── Commits ───────────────────────────────────────────────────
export const listCommits = (repoId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/repositories/${repoId}/commits/${qs ? '?' + qs : ''}`);
};
export const createCommit = (repoId, data) => request(`/repositories/${repoId}/commits/`, { method: 'POST', body: JSON.stringify(data) });

// ── Files ─────────────────────────────────────────────────────
export const listFiles = (repoId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/repositories/${repoId}/files/${qs ? '?' + qs : ''}`);
};
export const getFile = (repoId, fileId) => request(`/repositories/${repoId}/files/${fileId}/`);
export const createFile = (repoId, data) => request(`/repositories/${repoId}/files/`, { method: 'POST', body: JSON.stringify(data) });
export const updateFile = (repoId, fileId, data) => request(`/repositories/${repoId}/files/${fileId}/`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteFile = (repoId, fileId) => request(`/repositories/${repoId}/files/${fileId}/`, { method: 'DELETE' });

// ── Issues ────────────────────────────────────────────────────
export const listIssues = (repoId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/repositories/${repoId}/issues/${qs ? '?' + qs : ''}`);
};
export const getIssue = (repoId, issueId) => request(`/repositories/${repoId}/issues/${issueId}/`);
export const createIssue = (repoId, data) => request(`/repositories/${repoId}/issues/`, { method: 'POST', body: JSON.stringify(data) });
export const closeIssue = (repoId, issueId) => request(`/repositories/${repoId}/issues/${issueId}/close/`, { method: 'POST' });
export const reopenIssue = (repoId, issueId) => request(`/repositories/${repoId}/issues/${issueId}/reopen/`, { method: 'POST' });
export const getIssueComments = (repoId, issueId) => request(`/repositories/${repoId}/issues/${issueId}/comments/`);
export const addIssueComment = (repoId, issueId, data) => request(`/repositories/${repoId}/issues/${issueId}/comments/`, { method: 'POST', body: JSON.stringify(data) });

// ── Pull Requests ─────────────────────────────────────────────
export const listPullRequests = (repoId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/repositories/${repoId}/pulls/${qs ? '?' + qs : ''}`);
};
export const getPullRequest = (repoId, prId) => request(`/repositories/${repoId}/pulls/${prId}/`);
export const createPullRequest = (repoId, data) => request(`/repositories/${repoId}/pulls/`, { method: 'POST', body: JSON.stringify(data) });
export const mergePR = (repoId, prId) => request(`/repositories/${repoId}/pulls/${prId}/merge/`, { method: 'POST' });
export const closePR = (repoId, prId) => request(`/repositories/${repoId}/pulls/${prId}/close/`, { method: 'POST' });
export const getPRComments = (repoId, prId) => request(`/repositories/${repoId}/pulls/${prId}/comments/`);
export const addPRComment = (repoId, prId, data) => request(`/repositories/${repoId}/pulls/${prId}/comments/`, { method: 'POST', body: JSON.stringify(data) });

// ── Releases ──────────────────────────────────────────────────
export const listReleases = (repoId) => request(`/repositories/${repoId}/releases/`);
export const createRelease = (repoId, data) => request(`/repositories/${repoId}/releases/`, { method: 'POST', body: JSON.stringify(data) });
export const publishRelease = (repoId, releaseId) => request(`/repositories/${repoId}/releases/${releaseId}/publish/`, { method: 'POST' });
export const deleteRelease = (repoId, releaseId) => request(`/repositories/${repoId}/releases/${releaseId}/`, { method: 'DELETE' });

// ── Wiki ──────────────────────────────────────────────────────
export const getWiki = (repoId) => request(`/repositories/${repoId}/wiki/`);
export const listWikiPages = (repoId) => request(`/repositories/${repoId}/wiki/pages/`);
export const createWikiPage = (repoId, data) => request(`/repositories/${repoId}/wiki/pages/`, { method: 'POST', body: JSON.stringify(data) });
export const getWikiPage = (repoId, slug) => request(`/repositories/${repoId}/wiki/pages/${slug}/`);
export const updateWikiPage = (repoId, slug, data) => request(`/repositories/${repoId}/wiki/pages/${slug}/`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteWikiPage = (repoId, slug) => request(`/repositories/${repoId}/wiki/pages/${slug}/`, { method: 'DELETE' });

// ── Search ────────────────────────────────────────────────────
export const searchRepos = (q, type = 'repositories') => request(`/search/?q=${encodeURIComponent(q)}&type=${type}`);
